import { Component, EventEmitter, forwardRef, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CodeAndName, NumericCodeAndName } from '@lx/shared/types';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ListboxModule } from 'primeng/listbox';

@Component({
  selector: 'lx-dropdown-popup',
  standalone: true,
  imports: [DialogModule, DropdownModule, ListboxModule, FormsModule],
  templateUrl: './dropdown-popup.component.html',
  styleUrls: ['./dropdown-popup.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownPopupComponent),
      multi: true,
    },
  ],
})
export class DropdownPopupComponent<T extends (CodeAndName | NumericCodeAndName) & Record<string, unknown>, K extends keyof T>
  implements OnInit, OnChanges, ControlValueAccessor
{
  @HostBinding('class') private hostClass: string | undefined;
  @Input() itemList: T[] = [];
  @Input() company = '';
  @Input() visible = false;
  @Output() dropdownPopupClosed = new EventEmitter<void>();

  protected disabled = false;
  protected selectedItem: T | undefined;

  protected dialogVisible = false;

  private currentValue: T[K] | undefined;
  private onChange: (value: T[K] | undefined) => void = () => {
    // onChange triggered but not registered.
  };
  private onTouched: () => void = () => {
    // onTouched triggered but not registered.
  };

  ngOnInit(): void {
    this.hostClass = this.getCompanyClass();
  }

  ngOnChanges({ itemList, visible }: SimpleChanges): void {
    if (itemList?.currentValue && this.currentValue) {
      this.updateSelectedItem(this.currentValue);
    }

    if (visible?.currentValue !== undefined) {
      this.dialogVisible = visible.currentValue;
    }
  }

  writeValue(value: T[K] | undefined): void {
    this.currentValue = value;
    this.updateSelectedItem(value);
  }

  private updateSelectedItem(value: T[K] | undefined): void {
    // Find the full item object based on the code value
    this.selectedItem = this.itemList?.find((item: T) => item.code === value);
  }

  registerOnChange(fn: (value: T[K] | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  closePopup() {
    this.onTouched();
    this.dropdownPopupClosed.emit();
  }

  onOptionChange(item: T | undefined) {
    // Update internal selectedItem for UI display
    this.selectedItem = item;
    // Emit only the code value (T[K]) via ControlValueAccessor
    const codeValue = item?.code as T[K];
    this.onChange(codeValue);
    this.closePopup();
  }

  private getCompanyClass() {
    return `dropdown-popup--${this.company.toLowerCase()}`;
  }
}
